import type { WalletAsset } from "@/interfaces/asset";
import type { Token } from "@/interfaces/token";
import { useAppSelector } from "@/store/hooks";
import {
  formatEvmHumanBalance,
  normalizeCurrency,
  removeTrailingToken,
} from "@/utils";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TokenIcon from "../token-icon";

interface Props {
  asset: WalletAsset | Token;
  variant?: "wallet" | "add";
  onAdd?: () => void;
  isPending?: boolean;
  isLast?: boolean;
}

const AssetItem = ({
  asset,
  variant = "wallet",
  onAdd,
  isPending = false,
  isLast = false,
}: Props) => {
  const navigate = useNavigate();

  const { showBalance } = useAppSelector((state) => state.setting);

  const isWalletAsset = "token" in asset;
  const token = isWalletAsset ? asset.token : asset;

  const tokenName = token.name;
  const tokenSymbol = token.symbol;
  const iso = token.iso;

  /*
   * ADD VARIANT
   */
  if (variant === "add") {
    return (
      <div
        className={cn(
          "flex items-center justify-between px-2 py-2",
          "hover:bg-muted/40 transition",
          !isLast && "border-b",
        )}
      >
        <div className="flex items-center gap-3">
          <TokenIcon token={token} id={`${variant}-${asset.id}`} size="lg" />

          <div>
            <p className="text-sm font-medium">
              {removeTrailingToken(tokenName)}{" "}
              <span className="text-muted-foreground">({iso})</span>
            </p>
          </div>
        </div>

        <button
          type="button"
          disabled={isPending}
          onClick={onAdd}
          className={cn(
            "flex items-center gap-1 px-3 py-1.5 text-xs rounded-full",
            "bg-emerald-500/10 text-emerald-500",
            "hover:bg-emerald-500/20",
            "disabled:opacity-50 disabled:pointer-events-none",
          )}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
    );
  }

  /*
   * WALLET VARIANT
   *
   * If a Token was accidentally passed here, don't crash the
   * entire application. Render the token using zero balances.
   */
  const walletAsset = isWalletAsset ? asset : null;

  const available = walletAsset?.available ?? "0";
  const convertedBalance = walletAsset?.convertedBalance ?? "0";
  const convertedBalanceSymbol = walletAsset?.convertedBalanceSymbol ?? "";

  const currency = normalizeCurrency(walletAsset?.currency ?? iso ?? "");

  const handleClick = () => {
    if (!walletAsset) return;

    navigate(`/dashboard/wallet/${walletAsset.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-center justify-between rounded-xl",
        "border border-border bg-card p-3 shadow-sm",
        "transition-all duration-200",
        walletAsset && "hover:shadow-md hover:border-primary/20 cursor-pointer",
      )}
    >
      {/* LEFT */}
      <div className="flex items-center gap-3">
        <TokenIcon token={token} id={`${variant}-${asset.id}`} size="lg" />

        <div>
          <p className="font-semibold">
            {removeTrailingToken(tokenName)}{" "}
            <span className="text-muted-foreground">({currency})</span>
          </p>

          <p className="text-xs text-muted-foreground">
            {showBalance
              ? `≈ ${convertedBalanceSymbol}${formatEvmHumanBalance(
                  convertedBalance,
                  token.isNative,
                )}`
              : "****"}
          </p>
        </div>
      </div>

      {/* RIGHT */}
      <div className="text-right">
        <p className="text-lg font-semibold text-emerald-500">
          {showBalance
            ? `${tokenSymbol} ${formatEvmHumanBalance(
                available,
                token.isNative,
              )}`
            : "****"}
        </p>
      </div>
    </div>
  );
};

export default AssetItem;
